import type { GuildState } from '@/types/app-state';
import type {
  EventDetailView,
  EventListItem,
  MassLifecycleSummary,
  EventMass,
  EventPartyView,
  PartyCapacitySummary,
  EventSlot,
  EventSlotSummary,
  LiveEventWindow,
  RoleCategory,
  SignupParticipant,
  SignupHistoryGroup,
} from '@/types/domain';
import { getProfileInGameName } from '@/utils/profile';

function isActiveSignupStatus(status: string): boolean {
  return status === 'joined' || status === 'waitlist';
}

export interface EventRosterMetrics {
  totalJoined: number;
  totalWaitlist: number;
  totalRequired: number;
  remainingCount: number;
  activeSlotCount: number;
}

export function getLiveWindow(event: EventMass): LiveEventWindow {
  const now = Date.now();
  const opensAt = new Date(event.signupOpensAt).getTime();
  const closesAt = new Date(event.signupClosesAt).getTime();

  if (now < opensAt || now >= closesAt || event.status === 'closed' || event.status === 'archived') {
    return 'closed';
  }

  if (closesAt - now <= 2 * 3_600_000) {
    return 'closing_soon';
  }

  return 'open';
}

export function getMassLifecycleSummary(
  event: Pick<EventMass, 'status' | 'signupOpensAt' | 'signupClosesAt' | 'startsAt'>,
): MassLifecycleSummary {
  const now = Date.now();
  const signupOpensAt = new Date(event.signupOpensAt).getTime();
  const signupClosesAt = new Date(event.signupClosesAt).getTime();
  const startsAt = new Date(event.startsAt).getTime();

  if (event.status === 'draft') {
    return {
      stage: 'draft',
      label: 'Draft',
      tone: 'warning',
      isSignupAvailable: false,
      isStarted: false,
      isFinishedLike: false,
    };
  }

  if (event.status === 'cancelled') {
    return {
      stage: 'cancelled',
      label: 'Cancelled',
      tone: 'danger',
      isSignupAvailable: false,
      isStarted: false,
      isFinishedLike: true,
    };
  }

  if (event.status === 'archived') {
    return {
      stage: 'archived',
      label: 'Archived',
      tone: 'default',
      isSignupAvailable: false,
      isStarted: startsAt <= now,
      isFinishedLike: true,
    };
  }

  if (startsAt <= now) {
    return {
      stage: event.status === 'closed' ? 'finished' : 'in_progress',
      label: event.status === 'closed' ? 'Finished' : 'Mass Started',
      tone: event.status === 'closed' ? 'default' : 'warning',
      isSignupAvailable: false,
      isStarted: true,
      isFinishedLike: event.status === 'closed',
    };
  }

  if (signupOpensAt > now) {
    return {
      stage: 'signup_pending',
      label: 'Before Signup',
      tone: 'info',
      isSignupAvailable: false,
      isStarted: false,
      isFinishedLike: false,
    };
  }

  if (signupClosesAt <= now || event.status === 'closed') {
    return {
      stage: 'signup_closed',
      label: 'Signup Closed',
      tone: 'danger',
      isSignupAvailable: false,
      isStarted: false,
      isFinishedLike: false,
    };
  }

  if (signupClosesAt - now <= 2 * 3_600_000) {
    return {
      stage: 'signup_closing_soon',
      label: 'Closing Soon',
      tone: 'warning',
      isSignupAvailable: true,
      isStarted: false,
      isFinishedLike: false,
    };
  }

  return {
    stage: 'signup_open',
    label: 'Signup Open',
    tone: 'success',
    isSignupAvailable: true,
    isStarted: false,
    isFinishedLike: false,
  };
}

function buildSlotSummary(
  slot: EventSlot,
  state: GuildState,
  profilesById: Map<string, GuildState['profiles'][number]>,
  currentUserId?: string,
): EventSlotSummary {
  const slotSignups = state.signups.filter((signup) => signup.eventSlotId === slot.id && isActiveSignupStatus(signup.status));
  const joinedCount = slotSignups.filter((signup) => signup.status === 'joined').length;
  const waitlistCount = slotSignups.filter((signup) => signup.status === 'waitlist').length;

  const participants: SignupParticipant[] = slotSignups.map((signup) => {
    const profile = profilesById.get(signup.profileId);

    return {
      profileId: signup.profileId,
      displayName: profile?.displayName ?? 'Unknown Member',
      inGameName: getProfileInGameName(profile),
      discordUsername: profile?.discordUsername,
      signupId: signup.id,
      status: signup.status,
      queuePosition: signup.queuePosition,
      isCurrentUser: signup.profileId === currentUserId,
    };
  });

  const joinedMembers = participants
    .filter((participant) => participant.status === 'joined')
    .sort((left, right) => left.inGameName.localeCompare(right.inGameName));

  const waitlistMembers = participants
    .filter((participant) => participant.status === 'waitlist')
    .sort((left, right) => (left.queuePosition ?? Number.MAX_SAFE_INTEGER) - (right.queuePosition ?? Number.MAX_SAFE_INTEGER));

  return {
    ...slot,
    joinedCount,
    waitlistCount,
    isCurrentUserSignedHere: slotSignups.some((signup) => signup.profileId === currentUserId),
    isFull: joinedCount >= slot.capacity,
    joinedMembers,
    waitlistMembers,
  };
}

function buildPartySummary(slots: EventSlotSummary[]): PartyCapacitySummary {
  const activeSlots = slots.filter((slot) => slot.isActive);
  const totalCapacity = activeSlots.reduce((sum, slot) => sum + slot.capacity, 0);
  const joinedCount = activeSlots.reduce((sum, slot) => sum + slot.joinedCount, 0);
  const roleDistributionMap = new Map<RoleCategory, PartyCapacitySummary['roleDistribution'][number]>();

  for (const slot of activeSlots) {
    const existing = roleDistributionMap.get(slot.roleCategory) ?? {
      roleCategory: slot.roleCategory,
      joinedCount: 0,
      requiredCount: 0,
    };

    existing.joinedCount += slot.joinedCount;
    existing.requiredCount += slot.capacity;
    roleDistributionMap.set(slot.roleCategory, existing);
  }

  return {
    joinedCount,
    totalCapacity,
    remainingCount: Math.max(totalCapacity - joinedCount, 0),
    isFull: totalCapacity > 0 && joinedCount >= totalCapacity,
    roleDistribution: Array.from(roleDistributionMap.values()).sort((left, right) =>
      left.roleCategory.localeCompare(right.roleCategory),
    ),
  };
}

export function getEventRosterMetrics(state: GuildState, eventId: string): EventRosterMetrics {
  const activeSlots = state.eventSlots.filter((slot) => slot.eventId === eventId && slot.isActive);
  const activeSlotIds = new Set(activeSlots.map((slot) => slot.id));
  const activeSignups = state.signups.filter(
    (signup) => activeSlotIds.has(signup.eventSlotId) && isActiveSignupStatus(signup.status),
  );
  const totalRequired = activeSlots.reduce((sum, slot) => sum + slot.capacity, 0);
  const totalJoined = activeSignups.filter((signup) => signup.status === 'joined').length;
  const totalWaitlist = activeSignups.filter((signup) => signup.status === 'waitlist').length;

  return {
    totalJoined,
    totalWaitlist,
    totalRequired,
    remainingCount: Math.max(totalRequired - totalJoined, 0),
    activeSlotCount: activeSlots.length,
  };
}

export function getCurrentMasses(state: GuildState, currentUserId?: string): EventListItem[] {
  return state.events
    .filter((event) => event.status !== 'archived' && event.status !== 'cancelled' && event.status !== 'draft')
    .map((event) => {
      const metrics = getEventRosterMetrics(state, event.id);
      const currentUserSignup = currentUserId
        ? state.signups.find(
            (signup) => signup.eventId === event.id && signup.profileId === currentUserId && isActiveSignupStatus(signup.status),
          )
        : undefined;

      return {
        ...event,
        totalJoined: metrics.totalJoined,
        totalWaitlist: metrics.totalWaitlist,
        totalRequired: metrics.totalRequired,
        remainingCount: metrics.remainingCount,
        liveWindow: getLiveWindow(event),
        isFull: metrics.totalRequired > 0 && metrics.remainingCount <= 0,
        currentUserSignupStatus: currentUserSignup?.status,
      };
    })
    .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime());
}

export function getEventDetail(state: GuildState, eventId: string, currentUserId?: string): EventDetailView | undefined {
  const event =
    getCurrentMasses(state, currentUserId).find((item) => item.id === eventId) ??
    state.events
      .filter((item) => item.status === 'archived')
      .map((item) => {
        const metrics = getEventRosterMetrics(state, item.id);
        const currentUserSignup = currentUserId
          ? state.signups.find(
              (signup) => signup.eventId === item.id && signup.profileId === currentUserId && isActiveSignupStatus(signup.status),
            )
          : undefined;

        return {
          ...item,
          totalJoined: metrics.totalJoined,
          totalWaitlist: metrics.totalWaitlist,
          totalRequired: metrics.totalRequired,
          remainingCount: metrics.remainingCount,
          liveWindow: getLiveWindow(item),
          isFull: metrics.totalRequired > 0 && metrics.remainingCount <= 0,
          currentUserSignupStatus: currentUserSignup?.status,
        };
      })
      .find((item) => item.id === eventId);

  if (!event) {
    return undefined;
  }

  const profilesById = new Map(state.profiles.map((profile) => [profile.id, profile]));

  const parties: EventPartyView[] = state.eventParties
    .filter((party) => party.eventId === eventId)
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((party) => {
      const slots = state.eventSlots
        .filter((slot) => slot.eventPartyId === party.id)
        .sort((left, right) => left.slotOrder - right.slotOrder)
        .map((slot) => buildSlotSummary(slot, state, profilesById, currentUserId));

      return {
        party,
        slots,
        summary: buildPartySummary(slots),
      };
    });

  const currentUserSignup = state.signups.find(
    (signup) => signup.eventId === eventId && signup.profileId === currentUserId && isActiveSignupStatus(signup.status),
  );

  return {
    ...event,
    parties,
    currentUserSignup,
  };
}

export function getSignupHistoryForProfile(state: GuildState, profileId: string): SignupHistoryGroup[] {
  const groups = new Map<string, SignupHistoryGroup>();

  for (const signup of state.signups.filter((item) => item.profileId === profileId)) {
    const event = state.events.find((item) => item.id === signup.eventId);

    if (!event) {
      continue;
    }

    if (!groups.has(event.id)) {
      groups.set(event.id, { event, signups: [] });
    }

    groups.get(event.id)!.signups.push(signup);
  }

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      signups: [...group.signups].sort(
        (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
      ),
    }))
    .sort((left, right) => new Date(right.event.startsAt).getTime() - new Date(left.event.startsAt).getTime());
}
